import { inject } from "@angular/core";
import { KeyvalService } from "../keyval.service";
import { logger } from "../logger";
import { toError } from "../utils/to-error";
import { ApiRaw } from "../api-raw";
import { ListApiSearchRequest, ListApiSearchResponse, ListData, ListSearch } from "../lists";

interface ApiStub {
    id: string;
    name: string;
}

export interface Stub {
    name: string;
    label: string;
}

export abstract class CachedStore<T, K extends { id: string }> {
    abstract prefix: string;
    protected ttl = 60 * 100;
    private keyval = inject(KeyvalService);
    protected api = inject(ApiRaw);

    private memoryCache = new Map<string, K>();
    private pendingRequests = new Map<string, Promise<K>>();

    private pendingWrites: Map<string, { value: T; timestamp: number }> = new Map();
    private writeTimeout: any;

    async get(key: string): Promise<K> {
        if (this.memoryCache.has(key)) {
            return this.memoryCache.get(key)!;
        }

        if (this.pendingRequests.has(key)) {
            return this.pendingRequests.get(key)!;
        }

        const promise = this.fetch(key);
        this.pendingRequests.set(key, promise);
        try {
            const result = await promise;
            this.memoryCache.set(key, result);
            return result;
        } catch (error) {
            logger.error(`Failed fetching ${key}`, toError(error));
            throw error;
        } finally {
            this.pendingRequests.delete(key);
        }
    }

    async getMany(keys: string[]): Promise<K[]> {
        const promises = keys.map(key => this.get(key));
        return Promise.all(promises);
    }

    private async fetch(key: string): Promise<K> {
        const idbKey = `${this.prefix}:${key}`;
        const cached = await this.keyval.getItem(idbKey);
        if (cached && new Date().getTime() - cached.timestamp < this.ttl) {
            return this.fromApiResponse(cached.value);
        }

        const result = await this.fetchOne(key);
        this.persistentCache(idbKey, result);
        return this.fromApiResponse(result);
    }

    private async persistentCache(idbKey: string, value: T) {
        const payload = {
            value,
            timestamp: new Date().getTime()
        }
        this.pendingWrites.set(idbKey, payload);
        if (this.writeTimeout) {
            clearTimeout(this.writeTimeout);
        }
        this.writeTimeout = setTimeout(() => {
            const entries = Array.from(this.pendingWrites.entries());
            this.keyval.setMany(entries);
            this.pendingWrites.clear();
        }, 1000);
    }

    async searchStub(payload: any): Promise<Stub[]> {
        const response = this.api.post<ApiStub[]>(`/${this.prefix}/stub`, payload)
        return (await response).map(item => ({ name: item.id, label: item.name }))
    }

    async search(search: ListSearch): Promise<ListData<K>> {
        const apiSearch: ListApiSearchRequest = {
            filter: search.filter,
            sort: search.sort,
            pagination: {
                start: search.pagination.page * search.pagination.pageSize,
                size: search.pagination.pageSize
            }
        };
        const res = await this.api.post<ListApiSearchResponse<T>>(`/${this.prefix}/search`, apiSearch);
        return {
            items: res.docs.map(item => this.fromApiResponse(item)),
            meta: {
                totalItems: res.total,
            }
        };
    }

    protected abstract fetchOne(key: string): Promise<T>;
    protected abstract fromApiResponse(response: T): K;

}